import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import OpenInAppLink from '../connectivity/OpenInAppLink';
import DeviceLimitModal from '../components/DeviceLimitModal';

export default function LoginPage() {
  const { user, login, loginVerifyMfa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // ── 2FA step state ──
  const [mfaToken, setMfaToken] = useState(null);   // when set, render the OTP form
  const [mfaCode, setMfaCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);

  // ── Remember-device checkbox ──
  // Default TRUE — matches Google/Notion behaviour: normal users stay
  // signed in for 30 days. Un-tick it on a coworker's machine or in an
  // incognito test-drive → session becomes ephemeral (8-hour cookie) and
  // does NOT count against the clinic's tier device cap.
  const [rememberDevice, setRememberDevice] = useState(true);

  // ── Device-limit modal state ──
  // When the server returns 409 DEVICE_LIMIT_EXCEEDED, we surface a picker
  // so the user chooses which of their existing devices to sign out. The
  // retry then goes through the same code path with `replace_session_id`.
  const [deviceLimit, setDeviceLimit] = useState(null); // {cap, count, devices, path: 'login'|'mfa'}
  const [kickingSid, setKickingSid] = useState(null);

const roleHome = (role) => {
  if (['founder', 'super_admin', 'sales_manager', 'support_agent', 'finance_manager', 'product_ops', 'read_only'].includes(role)) return '/admin/dashboard';
  if (role === 'referral_partner') return '/partner';
  if (role === 'audiologist') return '/test';
  return '/patients';
};

  // Redirect if already authenticated (page reload scenario)
  useEffect(() => {
    if (user) {
      navigate(roleHome(user.role), { replace: true });
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr(null);
    try {
      const u = await login(email.trim(), password, { rememberDevice });
      if (u && u.requiresMfa) {
        setMfaToken(u.mfaToken);
        return;
      }
      navigate(roleHome(u.role), { replace: true });
    } catch (ex) {
      // Email verification gate — hard-block redirect to /verify-email
      if (ex?.emailNotVerified) {
        navigate(`/verify-email?email=${encodeURIComponent(ex.email || email)}`, { replace: true });
        return;
      }
      // Device-limit — show the picker modal.
      if (ex?.deviceLimitExceeded) {
        setDeviceLimit({
          cap: ex.cap, count: ex.count, devices: ex.devices, path: 'login',
        });
        return;
      }
      const d = ex?.response?.data?.detail;
      setErr(typeof d === 'string' ? d : (ex?.message || 'Login failed'));
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr(null);
    try {
      const u = await loginVerifyMfa(mfaToken, mfaCode.trim(), useRecovery, { rememberDevice });
      navigate(roleHome(u.role), { replace: true });
    } catch (ex) {
      if (ex?.deviceLimitExceeded) {
        setDeviceLimit({
          cap: ex.cap, count: ex.count, devices: ex.devices, path: 'mfa',
        });
        return;
      }
      const d = ex?.response?.data?.detail;
      setErr(typeof d === 'string' ? d : (ex?.message || 'Invalid code'));
    } finally {
      setBusy(false);
    }
  };

  // Handler invoked by DeviceLimitModal when the user picks a device to kick.
  // We retry the ORIGINAL login (or MFA) path with `replaceSessionId` set.
  const handleDeviceLimitPick = async (sessionId) => {
    setKickingSid(sessionId); setErr(null);
    try {
      let u;
      if (deviceLimit?.path === 'mfa') {
        u = await loginVerifyMfa(mfaToken, mfaCode.trim(), useRecovery, { replaceSessionId: sessionId, rememberDevice });
      } else {
        u = await login(email.trim(), password, { replaceSessionId: sessionId, rememberDevice });
        if (u && u.requiresMfa) {
          setMfaToken(u.mfaToken);
          setDeviceLimit(null);
          return;
        }
      }
      navigate(roleHome(u.role), { replace: true });
    } catch (ex) {
      if (ex?.deviceLimitExceeded) {
        // Server rejected the replace (session was already revoked etc)
        // — refresh the modal with the fresh device list.
        setDeviceLimit({
          cap: ex.cap, count: ex.count, devices: ex.devices,
          path: deviceLimit?.path || 'login',
        });
      } else {
        const d = ex?.response?.data?.detail;
        setErr(typeof d === 'string' ? d : (ex?.message || 'Sign out failed'));
        setDeviceLimit(null);
      }
    } finally {
      setKickingSid(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-6" data-testid="login-brand">
          <img
            src="/brand/audinexa-horizontal.png"
            alt="Audinexa — Hearing care connected"
            data-testid="login-brand-logo"
            className="mx-auto h-16 md:h-20 w-auto object-contain drop-shadow-[0_6px_20px_rgba(15,82,186,0.35)] bg-white/95 rounded-xl px-5 py-2.5"
          />
          <p className="text-[11px] tracking-[0.28em] uppercase text-slate-400 mt-3 font-semibold">
            Audiology practice management · India
          </p>
        </div>

        <form onSubmit={mfaToken ? submitMfa : submit} className="bg-white rounded-xl shadow-2xl p-6 space-y-4" data-testid="login-form">
          {mfaToken ? (
            <>
              <div>
                <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-2">
                  Two-factor authentication
                </div>
                <p className="text-[12.5px] text-slate-600">
                  Enter the 6-digit code from your authenticator app.
                </p>
              </div>
              <div>
                <input
                  type="text"
                  inputMode={useRecovery ? 'text' : 'numeric'}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(
                    useRecovery
                      ? (e.target.value.replace(/\s/g, '').slice(0, 12) || '').toUpperCase()
                      : e.target.value.replace(/\D/g, '').slice(0, 6)
                  )}
                  autoFocus
                  maxLength={useRecovery ? 12 : 6}
                  placeholder={useRecovery ? 'Recovery code (e.g. M6TJ8QT36D)' : '123 456'}
                  data-testid="login-mfa-code"
                  className="w-full px-3 py-3 text-lg font-mono tracking-widest text-center border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {err && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="login-error">{err}</div>
              )}

              <button
                type="submit"
                disabled={busy || mfaCode.length < (useRecovery ? 8 : 6)}
                data-testid="login-mfa-submit"
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-semibold text-sm rounded-lg shadow-md transition-colors"
              >
                {busy ? 'Verifying…' : 'Verify and sign in'}
              </button>

              <div className="text-center text-[12px]">
                <button
                  type="button"
                  onClick={() => { setUseRecovery(!useRecovery); setMfaCode(''); setErr(null); }}
                  data-testid="login-mfa-toggle-recovery"
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {useRecovery ? 'Use authenticator code instead' : 'Use a recovery code instead'}
                </button>
              </div>
              <div className="text-center text-[11px] text-slate-500">
                <button
                  type="button"
                  onClick={() => { setMfaToken(null); setMfaCode(''); setErr(null); setUseRecovery(false); }}
                  className="text-slate-500 hover:text-slate-700 hover:underline"
                >
                  ← Back to sign-in
                </button>
              </div>
            </>
          ) : (
            <>
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              required
              data-testid="login-email"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Password</label>
              <Link
                to="/forgot-password"
                data-testid="login-forgot-link"
                className="text-[11px] font-semibold text-blue-600 hover:text-blue-800 hover:underline"
              >
                Forgot password?
              </Link>
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {err && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="login-error">{err}</div>
          )}

          <label
            className="flex items-start gap-2 cursor-pointer select-none group"
            data-testid="login-remember-device-row"
          >
            <input
              type="checkbox"
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
              data-testid="login-remember-device"
              className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
            />
            <span className="text-[11.5px] leading-tight text-slate-600 group-hover:text-slate-800">
              <span className="font-semibold text-slate-700">Remember this device for 30 days</span>
              <span className="block text-[10.5px] text-slate-500 mt-0.5">
                {rememberDevice
                  ? 'Stays signed in on this device. Counts toward your plan\u2019s device limit.'
                  : 'Ephemeral session \u2014 signs out in 8 hours and does NOT use a device slot. Use for incognito test-drives.'}
              </span>
            </span>
          </label>

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-semibold text-sm rounded-lg shadow-md transition-colors"
          >
            {busy ? 'Signing in\u2026' : 'Sign in'}
          </button>

          <p className="text-center text-[11px] text-slate-500 pt-1" data-testid="login-forgot-username-hint">
            Forgot your email? Ask your clinic owner — they can see staff emails in <i>Settings → Staff</i>.
          </p>
            </>
          )}
        </form>

        <div className="text-center mt-4 text-[10px] text-slate-500 flex items-center justify-center gap-2">
          <span>v14.0 · Audinexa · Hyderabad</span>
          <span className="text-slate-700" aria-hidden="true">·</span>
          <OpenInAppLink />
        </div>
      </div>

      {deviceLimit && (
        <DeviceLimitModal
          devices={deviceLimit.devices}
          cap={deviceLimit.cap}
          count={deviceLimit.count}
          busySid={kickingSid}
          onPick={handleDeviceLimitPick}
          onCancel={() => setDeviceLimit(null)}
        />
      )}
    </div>
  );
}
